/**
 * Cost-capped idle / background Inbox refill.
 *
 * Loosens the 2026-09-15 away freeze for Inbox only. A closed gap may add a
 * few Inbox RFQs while the desk is open and idle. It does not scale with the
 * gap, does not catch the schedule up, and does not invent empty ferries.
 */
import { isEmptyFerry, type RevenueClassifiable } from "./ledger";

export const IDLE_INBOX_MAX_RFQS = { min: 1, max: 6, default: 2 } as const;
export const IDLE_INBOX_COOLDOWN_MIN = { min: 15, max: 240, default: 45 } as const;
export const IDLE_INBOX_DAILY_CALLS = { min: 2, max: 24, default: 6 } as const;
export const IDLE_INBOX_WINDOW_MIN = { min: 60, max: 24 * 60, default: 12 * 60 } as const;

/** Desk must be idle this long before a while-open background mint. Reopen seed does not wait. */
export const IDLE_INBOX_AFTER_MIN = 15;

export const IDLE_INBOX_MAX_CHOICES = [1, 2, 3, 4, 6] as const;
export const IDLE_INBOX_COOLDOWN_CHOICES = [15, 30, 45, 60, 90, 120] as const;
export const IDLE_INBOX_DAILY_CALL_CHOICES = [2, 4, 6, 8, 12, 24] as const;
export const IDLE_INBOX_WINDOW_CHOICES = [180, 360, 720, 1440] as const;

export type IdleInboxSettings = {
  /**
   * On (default): while the desk is open and idle, or just back from away,
   * mint a bounded Inbox refill. Off: 2026-09-15 freeze — one reopen RFQ only.
   */
  enabled: boolean;
  /** Max new Inbox RFQs in one idle window. Not proportional to the away gap. */
  maxRfqsPerWindow: number;
  /** Minutes between idle mints inside a window. */
  cooldownMin: number;
  /** Cheap Grok calls allowed per UTC day. A spent budget does not fall through to Classic. */
  dailyCallBudget: number;
  /** How long the RFQ counter stays in force before it resets. */
  windowMin: number;
};

export type IdleInboxClock = {
  windowStartedAtUtc: string | null;
  windowMinted: number;
  /** UTC day `YYYY-MM-DD` the call counter belongs to. */
  callsDate: string | null;
  callsUsed: number;
};

export const DEFAULT_IDLE_INBOX: IdleInboxSettings = {
  enabled: true,
  maxRfqsPerWindow: IDLE_INBOX_MAX_RFQS.default,
  cooldownMin: IDLE_INBOX_COOLDOWN_MIN.default,
  dailyCallBudget: IDLE_INBOX_DAILY_CALLS.default,
  windowMin: IDLE_INBOX_WINDOW_MIN.default,
};

export const EMPTY_IDLE_INBOX_CLOCK: IdleInboxClock = {
  windowStartedAtUtc: null,
  windowMinted: 0,
  callsDate: null,
  callsUsed: 0,
};

function clampInt(value: unknown, min: number, max: number, fallback: number): number {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
}

function nearestChoice(value: number, choices: readonly number[], fallback: number): number {
  let best = fallback;
  let bestDist = Number.POSITIVE_INFINITY;
  for (const choice of choices) {
    const dist = Math.abs(choice - value);
    if (dist < bestDist) {
      best = choice;
      bestDist = dist;
    }
  }
  return best;
}

export function idleCallsDateKey(now: Date): string {
  return now.toISOString().slice(0, 10);
}

export function idleWindowCaption(windowMin: number): string {
  const hours = Math.round(windowMin / 60);
  return hours === 1 ? "1 h" : `${hours} h`;
}

export function hydrateIdleInboxSettings(raw?: Partial<IdleInboxSettings> | null): IdleInboxSettings {
  const max = nearestChoice(
    clampInt(raw?.maxRfqsPerWindow, IDLE_INBOX_MAX_RFQS.min, IDLE_INBOX_MAX_RFQS.max, IDLE_INBOX_MAX_RFQS.default),
    IDLE_INBOX_MAX_CHOICES,
    IDLE_INBOX_MAX_RFQS.default,
  );
  const cooldown = nearestChoice(
    clampInt(raw?.cooldownMin, IDLE_INBOX_COOLDOWN_MIN.min, IDLE_INBOX_COOLDOWN_MIN.max, IDLE_INBOX_COOLDOWN_MIN.default),
    IDLE_INBOX_COOLDOWN_CHOICES,
    IDLE_INBOX_COOLDOWN_MIN.default,
  );
  const calls = nearestChoice(
    clampInt(raw?.dailyCallBudget, IDLE_INBOX_DAILY_CALLS.min, IDLE_INBOX_DAILY_CALLS.max, IDLE_INBOX_DAILY_CALLS.default),
    IDLE_INBOX_DAILY_CALL_CHOICES,
    IDLE_INBOX_DAILY_CALLS.default,
  );
  const windowMin = nearestChoice(
    clampInt(raw?.windowMin, IDLE_INBOX_WINDOW_MIN.min, IDLE_INBOX_WINDOW_MIN.max, IDLE_INBOX_WINDOW_MIN.default),
    IDLE_INBOX_WINDOW_CHOICES,
    IDLE_INBOX_WINDOW_MIN.default,
  );
  return {
    enabled: raw?.enabled !== false,
    maxRfqsPerWindow: max,
    cooldownMin: cooldown,
    dailyCallBudget: calls,
    windowMin,
  };
}

export function hydrateIdleInboxClock(raw?: Partial<IdleInboxClock> | null): IdleInboxClock {
  const started = raw?.windowStartedAtUtc;
  let windowStartedAtUtc: string | null = null;
  if (typeof started === "string" && started.trim()) {
    const t = new Date(started);
    if (!Number.isNaN(t.getTime())) windowStartedAtUtc = t.toISOString();
  }
  const minted = clampInt(raw?.windowMinted, 0, 99, 0);
  const callsDate = typeof raw?.callsDate === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw.callsDate) ? raw.callsDate : null;
  const callsUsed = clampInt(raw?.callsUsed, 0, 999, 0);
  return { windowStartedAtUtc, windowMinted: minted, callsDate, callsUsed };
}

export function idleWindowRoom(
  clock: IdleInboxClock,
  now: Date,
  settings: IdleInboxSettings,
): { room: number; windowStartedAtUtc: string | null; windowMinted: number; fresh: boolean } {
  const max = settings.maxRfqsPerWindow;
  const started = clock.windowStartedAtUtc;
  if (!started) return { room: max, windowStartedAtUtc: null, windowMinted: 0, fresh: true };
  const elapsed = (now.getTime() - new Date(started).getTime()) / 60_000;
  if (!Number.isFinite(elapsed) || elapsed >= settings.windowMin) {
    return { room: max, windowStartedAtUtc: null, windowMinted: 0, fresh: true };
  }
  return {
    room: Math.max(0, max - clock.windowMinted),
    windowStartedAtUtc: started,
    windowMinted: clock.windowMinted,
    fresh: false,
  };
}

export function idleCallsRoom(clock: IdleInboxClock, now: Date, settings: IdleInboxSettings): number {
  const key = idleCallsDateKey(now);
  const used = clock.callsDate === key ? clock.callsUsed : 0;
  return Math.max(0, settings.dailyCallBudget - used);
}

/** Advance the window and the UTC-day call counter after a tick that was allowed to run. */
export function commitIdleMint(
  clock: IdleInboxClock,
  now: Date,
  settings: IdleInboxSettings,
  added: number,
  calls: number,
): IdleInboxClock {
  const window = idleWindowRoom(clock, now, settings);
  const key = idleCallsDateKey(now);
  const used = clock.callsDate === key ? clock.callsUsed : 0;
  const minted = Math.max(0, added);
  return {
    windowStartedAtUtc: minted > 0 ? (window.fresh ? now.toISOString() : window.windowStartedAtUtc) : window.fresh ? null : window.windowStartedAtUtc,
    windowMinted: (window.fresh ? 0 : window.windowMinted) + minted,
    callsDate: key,
    callsUsed: used + Math.max(0, calls),
  };
}

/** This path never invents an empty company / mx ferry. Paying charters stay. */
export function dropIdleFerries<T extends RevenueClassifiable>(rows: readonly T[]): T[] {
  return rows.filter((row) => !isEmptyFerry(row));
}
